import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import { IGamePlayer, IDeath } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { classImages } from 'utilities/ClassImages';
import { getAbility } from 'utilities/ability_icons';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { TeamColorsOpacity } from 'utilities/team_colors';
import { Sword } from 'components/SvgIcons/Sword';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap

const useStyles = makeStyles(theme => ({
  death: {
    marginBottom: 6,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '24px minmax(0, 1fr) minmax(0, 1fr) auto 24px',
    gridGap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'filter 0.12s',
    '&:hover': {
      filter: 'brightness(1.25)',
    },
  },
  open: {
    borderRadius: '6px 6px 0 0',
  },
  icon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
  },
  name: {
    fontWeight: 600,
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sword: {
    width: 14,
    height: 14,
    fill: 'rgba(255,255,255,0.55)',
    justifySelf: 'center',
  },
  damage: {
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: '0.75rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    background: 'rgba(0,0,0,0.3)',
    color: 'rgba(255,255,255,0.85)',
  },
  logs: {
    padding: theme.spacing(0.75),
    borderRadius: '0 0 6px 6px',
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderTop: 'none',
  },
  hit: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '22px minmax(0, 1fr) auto 18px 92px',
    gridGap: theme.spacing(1),
    alignItems: 'center',
    padding: '3px 6px',
    borderRadius: 4,
    fontSize: '0.8rem',
    overflow: 'hidden',
    '& > *': {
      position: 'relative',
    },
  },
  hitBar: {
    position: 'absolute !important' as any,
    left: 0,
    top: 0,
    bottom: 0,
    opacity: 0.22,
  },
  hitIcon: {
    width: 22,
    height: 22,
    borderRadius: 3,
  },
  hitAbility: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  hitPlayer: {
    opacity: 0.7,
    whiteSpace: 'nowrap',
  },
  hitValue: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
}));

/** A dark row tinted with the killer's team color on the left and the victim's on the right, split on a diagonal,
 * with a solid edge of each team color. */
const deathStyle = (killerTeam: number, victimTeam: number): React.CSSProperties => ({
  background: `linear-gradient(90deg, rgba(${TeamColorsOpacity[killerTeam]},0.3) 0%, rgba(${TeamColorsOpacity[killerTeam]},0.3) 50%, rgba(${TeamColorsOpacity[victimTeam]},0.3) 50%, rgba(${TeamColorsOpacity[victimTeam]},0.3) 100%), rgba(26, 28, 34, 0.96)`,
  boxShadow: `inset 3px 0 0 rgb(${TeamColorsOpacity[killerTeam]}), inset -3px 0 0 rgb(${TeamColorsOpacity[victimTeam]})`,
});

interface Props{
  deaths: IDeath[] | undefined;
  players: IGamePlayer[] | undefined;
  style?: React.CSSProperties;
}


const DeathBreakdownComponent: React.FC<Props> = ({deaths,players,style}) => {
  const classes = useStyles();

  let [openIndex, setOpenIndex] = useState(-1);

  return (
    <React.Fragment>
      <div className="death-breakdown" style={{gap: 0, ...style}}>
        {deaths?.map((death, deathIndex)=>{
            let foundVictim: IGamePlayer | undefined = players?.find((player: IGamePlayer)=>player.name == death.victim?.name);
            let foundKiller: IGamePlayer | undefined = players?.find((player: IGamePlayer)=>player.name == death.killed_by?.name);
            let victimClass = foundVictim ? foundVictim.class : "";
            let killerClass = foundKiller ? foundKiller.class : "";
            const open = openIndex == deathIndex;
            const expand = () => setOpenIndex(open ? -1 : deathIndex);
            return <div key={`death${deathIndex}`} className={classes.death}>
              <div className={`${classes.row} ${open ? classes.open : ''} noselect`} style={deathStyle(death.killed_by?.team || 0, death.victim?.team || 0)} onClick={expand}
                title="Click for the damage that led to this death">
                <img className={classes.icon} src={classImages.getColorIcon(killerClass as AocClasses)} title={killerClass} />
                <span className={classes.name}>{death.killed_by?.name}</span>
                <span className={classes.name} style={{textAlign: 'right'}}>{death.victim?.name}</span>
                <span className={classes.damage} title="Damage taken in this life">{(death.damageTaken || 0).toLocaleString()}</span>
                <img className={classes.icon} src={classImages.getColorIcon(victimClass as AocClasses)} title={victimClass} />
              </div>
              {open &&
                <div className={`${classes.logs} noselect`}>
                  {[...death.damage || []]?.sort((a,b)=> (b?.value || 0) - (a?.value || 0)).map((damage,index)=>{
                    let foundAttacker: IGamePlayer | undefined = players?.find((player: IGamePlayer)=>player.name == damage.name);
                    let c = foundAttacker ? foundAttacker.class : "";
                    let image = getAbility(damage.ability,c);
                    let percentage = Math.round(((damage.value || 0) / (death.damageTaken || 1) * 100));
                    return foundAttacker ?
                      <div className={classes.hit} key={`${damage.ability}${damage.value}${index}`}>
                        <div className={classes.hitBar} style={{backgroundColor:`rgb(${TeamColorsOpacity[foundAttacker?.team || 0]})`, width: `${percentage}%`}} />
                        <img className={classes.hitIcon} src={image} title={damage.ability} />
                        <span className={classes.hitAbility}>{damage.ability}{damage.count ? <span style={{opacity: 0.6}}> ×{damage.count}</span> : null}</span>
                        <span className={classes.hitPlayer}>{damage.name}</span>
                        <img className={classes.icon} style={{width: 18, height: 18}} src={classImages.getColorIcon(c as AocClasses)} title={c} />
                        <span className={classes.hitValue}><b>{(damage.value || 0).toLocaleString()}</b> <span style={{opacity: 0.6}}>{percentage}%</span></span>
                      </div>
                    : null;
                  })}
                </div>
              }
            </div>
          })
        }
      </div>
    </React.Fragment>
  )
}

// Memoized: the game views re-render several times a second (position / team location ticks)
// while these props only change when the game data itself changes.
export const DeathBreakdown = React.memo(DeathBreakdownComponent);
